// 一条回答：过程（理解问题、查资料、推理，做到哪一步就亮哪一步）、正文（Markdown，引用标签变成编号角标）、依据、复制。

import { useMemo, useState, type ReactNode } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { App as AntApp, Popover } from 'antd'
import { AlertCircle, Check, ChevronDown, Copy, FileText, Globe, Layers, Telescope } from 'lucide-react'
import { Orb, orbFor, useElapsed } from '../../ui/Thinking'
import { citeIndexOf, holdBackPartialTag, sourcesOf, stripCitations, withCitations, type CitationSource, type Cite } from '../../lib/citations'
import type { Reference } from '../../api/types'
import { IconButton, Pill } from '../../ui'
import { isProtectedFile } from '../../api/kb'
import { ProtectedImage } from '../kb/MarkdownDoc'
import { useDocPreview } from '../kb/usePreview'
import { cleanTitle } from '../kb/DocCard'
import type { Step, Turn } from './useChat'
import { useAllScopeKbs } from './Composer'
import { useSourceTag } from './useRoute'

/** Markdown 里只放行 http(s)、站内地址和引用角标；其余（javascript: 之类）一律不当链接。 */
const safeHref = (href?: string) => (href && (/^https?:\/\//i.test(href) || href.startsWith('/') || href.startsWith('#')) ? href : undefined)

function CitePopover({ n, cite, source, children }: { n: number; cite?: Cite; source?: CitationSource; children: ReactNode }) {
  const { open } = useDocPreview()
  const content = (
    <div className="cite-pop">
      <b>{source?.title ?? `来源 ${n}`}</b>
      {cite?.snippet && <p>{cite.snippet}</p>}
      {source?.kind === 'kb' && source.knowledgeId && (
        <div>
          <Pill size="sm" icon={<FileText />} onClick={() => open(source.knowledgeId!, source.kbId)}>
            打开这篇资料
          </Pill>
        </div>
      )}
      {source?.kind === 'web' && source.url && (
        <a className="sk-pill" data-size="sm" data-variant="soft" href={source.url} target="_blank" rel="noopener noreferrer">
          <Globe width={14} height={14} />
          打开网页
        </a>
      )}
    </div>
  )
  return (
    <Popover content={content} trigger={['click', 'hover']} mouseEnterDelay={0.25} placement="top">
      {children}
    </Popover>
  )
}

export interface AnswerProps {
  turn: Turn
  /** 这一问缩小过范围：按全部资料再问一次。 */
  onWiden?: () => void
  /** 把这一问交给深入研究。 */
  onResearch?: () => void
}

export function Answer({ turn, onWiden, onResearch }: AnswerProps) {
  const { message } = AntApp.useApp()
  const { open } = useDocPreview()
  const tagOf = useSourceTag((useAllScopeKbs() ?? []).map((k) => k.id))
  const routed = turn.steps?.some((s) => s.name === 'route')
  const text = turn.streaming ? holdBackPartialTag(turn.text) : turn.text
  const cited = useMemo(() => withCitations(text, turn.references ?? []), [text, turn.references])
  const sources = cited.sources.length ? cited.sources : turn.streaming ? [] : sourcesOf(turn.references ?? [])

  const components: Components = useMemo(
    () => ({
      a: ({ href, children }) => {
        const i = citeIndexOf(href, cited.nonce)
        if (i !== null) {
          const cite = cited.cites[i]
          const n = (cite?.source ?? 0) + 1
          return (
            <CitePopover n={n} cite={cite} source={cited.sources[cite?.source ?? -1]}>
              <button type="button" className="sk-cite" aria-label={`引用 ${n}：${cited.sources[cite?.source ?? -1]?.title ?? ''}`}>
                {n}
              </button>
            </CitePopover>
          )
        }
        const safe = safeHref(href)
        if (!safe) return <>{children}</>
        return safe.startsWith('http') ? (
          <a href={safe} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ) : (
          <a href={safe}>{children}</a>
        )
      },
      // 回答里引用了资料里的图：受保护的走灵智原生的文件代理，https 的直接显示；别的（本机路径之类）不显示
      img: ({ src, alt }) => {
        const raw = typeof src === 'string' ? src : ''
        if (isProtectedFile(raw)) return <ProtectedImage src={raw} alt={alt} />
        if (/^https:\/\//i.test(raw)) return <img src={raw} alt={alt ?? ''} loading="lazy" referrerPolicy="no-referrer" />
        return null
      },
    }),
    [cited],
  )

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stripCitations(turn.text).trim())
      message.success('已复制')
    } catch {
      message.error('没能复制，浏览器不让读写剪贴板')
    }
  }

  return (
    <article className="answer" aria-busy={turn.streaming} aria-live={turn.streaming ? 'polite' : undefined}>
      <Trace turn={turn} />
      {cited.markdown && (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} skipHtml urlTransform={(u) => u}>
          {cited.markdown}
        </ReactMarkdown>
      )}
      {turn.stopped && <p className="answer-note">回答已停止</p>}
      {turn.error && (
        <p className="answer-error" role="alert">
          <AlertCircle width={16} height={16} />
          {turn.error}
        </p>
      )}
      {!turn.streaming && turn.text && (
        <div className="answer-acts">
          <IconButton label="复制回答" onClick={copy}>
            <Copy />
          </IconButton>
          {routed && onWiden && (
            <Pill size="sm" variant="ghost" icon={<Layers />} onClick={onWiden} title="不缩小范围，按原来选的资料再问一次">
              改查全部资料
            </Pill>
          )}
          {onResearch && (
            <Pill size="sm" variant="ghost" icon={<Telescope />} onClick={onResearch} title="拆成几个小问题分头查资料，再写成一份带出处的报告（约 1 分钟）">
              展开研究
            </Pill>
          )}
        </div>
      )}
      {sources.length > 0 && (
        <section className="basis" aria-label="依据">
          <h2>依据</h2>
          {sources.map((s, i) =>
            s.kind === 'web' && s.url ? (
              <a key={i} className="ref" href={s.url} target="_blank" rel="noopener noreferrer">
                <span className="sk-cite">{i + 1}</span>
                <span className="t">{s.title}</span>
                <Globe width={14} height={14} />
              </a>
            ) : (
              <button key={i} type="button" className="ref" disabled={!s.knowledgeId} onClick={() => s.knowledgeId && open(s.knowledgeId, s.kbId)}>
                <span className="sk-cite">{i + 1}</span>
                <span className="t">{cleanTitle(s.title)}</span>
                <SourceTag tag={tagOf(s)} />
                <FileText width={14} height={14} />
              </button>
            ),
          )}
        </section>
      )}
    </article>
  )
}

/** 依据旁边的小标签：我写的、项目。 */
export function SourceTag({ tag }: { tag: string | null }) {
  return tag ? <span className="ref-tag" data-mine={tag === '我写的'}>{tag}</span> : null
}

// ———— 过程 ————

const STEP_LABEL: Record<string, string> = {
  query_understand: '理解问题',
  knowledge_search: '查资料',
  grep_chunks: '在原文里找',
  list_knowledge_chunks: '读资料',
  get_document_info: '看资料信息',
  web_search: '联网搜索',
  web_fetch: '读网页',
  wiki_search: '查 Wiki',
  thinking: '想一想',
}
const stepLabel = (s: Step) => s.label ?? STEP_LABEL[s.name] ?? s.name

/**
 * 回答之前的过程：进行中展开，一步一行（做完的打勾，正在做的转圈）；标题是点阵小球（按正在做的事换动作）+ 流光的字 + 走着的秒数；
 * 回答开始写以后收成一行「查了 N 段资料 · 想了 N 秒」，点开再看。参考 beautifului.dev 的 Thinking 和 Loading State。
 */
function Trace({ turn }: { turn: Turn }) {
  const [open, setOpen] = useState(false)
  const [start] = useState(() => Date.now())
  const steps = turn.steps ?? []
  const live = !!turn.streaming && !turn.text && !turn.error
  const elapsed = useElapsed(live ? start : null)
  if (!live && !steps.length && !turn.thinking) return null
  const seconds = turn.thoughtMs !== undefined ? Math.max(1, Math.round(turn.thoughtMs / 1000)) : null
  const pending = steps.find((s) => !s.done)
  const busy = steps.some((s) => s.name !== 'route')
  const now = pending ? stepLabel(pending) : turn.thinking ? '在想' : busy ? '准备回答' : '在看你的问题'
  const motion = orbFor(pending ? pending.name : turn.thinking ? 'thinking' : busy ? 'answer' : undefined)
  const found = turn.references?.length ?? 0
  const route = steps.find((s) => s.name === 'route')
  const tools = steps.filter((s) => s.name !== 'route')
  const summary = [route?.label ?? '', found ? `查了 ${found} 段资料` : tools.length ? `${tools.length} 步` : '', seconds ? `想了 ${seconds} 秒` : ''].filter(Boolean).join(' · ') || '过程'
  const shown = live || open
  return (
    <div className="trace" data-live={live}>
      <button type="button" className="trace-h" aria-expanded={shown} onClick={() => setOpen((v) => !v)} disabled={live}>
        {live && <Orb state={motion} className="trace-icon" />}
        {live ? (
          <>
            <span className="shimmer" role="status">
              {now}…
            </span>
            <span className="trace-t" aria-hidden="true">
              {elapsed.toFixed(1)}s
            </span>
          </>
        ) : (
          <span>{summary}</span>
        )}
        {!live && <ChevronDown className="chev" aria-hidden="true" />}
      </button>
      <div className="trace-wrap" data-open={shown}>
        <div className="trace-in">
          <ol className="trace-steps">
            {steps.map((s, i) => (
              <li key={i} data-done={s.done}>
                {s.done ? <Check className="ok" aria-hidden="true" /> : <i className="ring" aria-hidden="true" />}
                <span>
                  {stepLabel(s)}
                  {s.done && s.name === 'knowledge_search' && found > 0 && <small>找到 {found} 段</small>}
                  {s.done && s.ok === false && <small>没成功</small>}
                </span>
              </li>
            ))}
            {turn.thinking && (
              <li data-done={!live}>
                {live ? <i className="ring" aria-hidden="true" /> : <Check className="ok" aria-hidden="true" />}
                <span>
                  推理{seconds ? <small>{seconds} 秒</small> : null}
                  <span className="trace-thought" data-live={live}>
                    <span>{turn.thinking}</span>
                  </span>
                </span>
              </li>
            )}
          </ol>
        </div>
      </div>
    </div>
  )
}

export type { Reference }
